import { resolveOrgAnalyticRules } from '../../../domains/orgchart/assistant/orgAnalyticLensRules';
import { useMemo } from 'react';
import { create } from 'zustand';
import * as Y from 'yjs';
import type {
  ViewRule,
  ViewCondition,
  FilterEffect,
  DisplayRuleType,
  DisplayOptions,
  ViewRulePreset,
  FullPreset,
  ViewRuleKind,
} from '../models/ViewRules';
import {
  PRIMARY_FILTER_ID,
  createPrimaryFilterRule,
  createDisplayRule,
  createHeatmapRule,
  createColorByRule,
  createBadgeRule,
  createHighlightRule,
  createLabelRule,
  createFilterRule,
  getRuleKind,
  stripBlockedDisplayFields,
} from '../models/ViewRules';

/**
 * Generate a unique ID for rules and conditions
 */
function generateId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Built-in viewpoint presets that ship with the product.
 * Cannot be deleted by users (id starts with 'builtin-').
 */
const BUILT_IN_PRESETS: FullPreset[] = [
  {
    id: 'builtin-overview',
    name: 'Overview',
    rules: [
      { name: 'Primary Filter', conditions: [], logic: 'and', enabled: true, display: {}, order: 0 },
    ],
    createdAt: 0,
    requiredFields: [],
  },
  {
    id: 'builtin-department-colors',
    name: 'Department Colors',
    domain: 'orgchart',
    rules: [
      { name: 'Primary Filter', conditions: [], logic: 'and', enabled: true, display: {}, order: 0 },
      { name: 'Department Colors', conditions: [], logic: 'and', enabled: true, display: { colorByField: { field: 'department' } }, order: 1 },
    ],
    createdAt: 0,
    requiredFields: [],
  },
  {
    id: 'builtin-span-of-control',
    name: 'Span of Control',
    domain: 'orgchart',
    rules: [
      { name: 'Primary Filter', conditions: [], logic: 'and', enabled: true, display: {}, order: 0 },
      { name: 'Span of Control', conditions: [], logic: 'and', enabled: true, display: { heatmap: { field: 'directReports', minColor: '#dbeafe', maxColor: '#1e40af' } }, order: 1 },
    ],
    createdAt: 0,
    requiredFields: [],
  },
  {
    id: 'builtin-tenure',
    name: 'Tenure Distribution',
    domain: 'orgchart',
    rules: [
      { name: 'Primary Filter', conditions: [], logic: 'and', enabled: true, display: {}, order: 0 },
      { name: 'Tenure', conditions: [], logic: 'and', enabled: true, display: { heatmap: { field: 'tenure', minColor: '#dbeafe', maxColor: '#1e40af' } }, order: 1 },
    ],
    createdAt: 0,
    requiredFields: ['startDate'],
  },
  {
    id: 'builtin-flight-risk',
    name: 'Flight Risk',
    domain: 'orgchart',
    rules: [
      { name: 'Primary Filter', conditions: [], logic: 'and', enabled: true, display: {}, order: 0 },
      { name: 'Flight Risk', conditions: [], logic: 'and', enabled: true, display: { colorByField: { field: 'flightRisk', valueColors: { low: '#dcfce7', medium: '#fef3c7', high: '#fee2e2' } } }, order: 1 },
    ],
    createdAt: 0,
    requiredFields: ['flightRisk'],
  },
  {
    id: 'builtin-performance',
    name: 'Performance Distribution',
    domain: 'orgchart',
    rules: [
      { name: 'Primary Filter', conditions: [], logic: 'and', enabled: true, display: {}, order: 0 },
      { name: 'Performance', conditions: [], logic: 'and', enabled: true, display: { colorByField: { field: 'performance' } }, order: 1 },
    ],
    createdAt: 0,
    requiredFields: ['performance'],
  },
  {
    id: 'builtin-compensation',
    name: 'Compensation Bands',
    domain: 'orgchart',
    rules: [
      { name: 'Primary Filter', conditions: [], logic: 'and', enabled: true, display: {}, order: 0 },
      { name: 'Compensation Bands', conditions: [], logic: 'and', enabled: true, display: { heatmap: { field: 'salary', minColor: '#dcfce7', maxColor: '#7c3aed' } }, order: 1 },
    ],
    createdAt: 0,
    // `band` included so a banded org that withholds raw salary still gets the
    // lens offered rather than silently losing it.
    requiredFields: ['salary', 'compaRatio', 'band'],
  },
  {
    id: 'builtin-succession',
    name: 'Succession Readiness',
    domain: 'orgchart',
    rules: [
      { name: 'Primary Filter', conditions: [], logic: 'and', enabled: true, display: {}, order: 0 },
      {
        name: 'Succession Lens — Risk',
        conditions: [],
        logic: 'and',
        enabled: true,
        display: {
          colorByField: {
            field: 'successionRisk',
            valueColors: {
              critical: '#FBE6E6',
              high: '#FFF0EE',
              medium: '#FDF3D8',
              low: '#E3F7EE',
            },
          },
          successorLens: { visibility: 'selection' },
        },
        order: 1,
      },
      {
        name: 'Succession Lens — Key position',
        conditions: [{
          id: 'succession-lens-key-position',
          field: 'keyPosition',
          fieldType: 'boolean',
          operator: 'equals',
          value: true,
        }],
        logic: 'and',
        enabled: true,
        display: {
          scopeMode: 'custom',
          badge: {
            icon: 'Star',
            color: '#f59e0b',
            position: 'top-right',
            tooltip: 'Key position',
          },
        },
        order: 2,
      },
    ],
    createdAt: 0,
    requiredFields: ['successionRisk', 'keyPosition'],
  },
  {
    id: 'builtin-vacancy',
    name: 'Vacancy Highlight',
    domain: 'orgchart',
    rules: [
      { name: 'Primary Filter', conditions: [], logic: 'and', enabled: true, display: {}, order: 0 },
      { name: 'Vacancy Highlight', conditions: [], logic: 'and', enabled: true, display: { colors: { backgroundColor: '#fef3c7', borderColor: '#f59e0b' } }, order: 1 },
    ],
    createdAt: 0,
    requiredFields: ['isVacant'],
  },
];

/**
 * Domain-contributed presets. Populated at runtime via
 * `registerDomainPresets()` — e.g. the network domain registers its
 * Security/Compute/Data/IAM presets from `registerNetworkDomain()`.
 *
 * Kept separate from BUILT_IN_PRESETS so core doesn't own domain
 * content. `filterPresetsByDomain` narrows by the active diagram's
 * domain, so domain-agnostic presets from core and domain presets
 * both reach the UI through the same spread.
 */
const DOMAIN_PRESETS: FullPreset[] = [];

export function registerDomainPresets(presets: FullPreset[]): void {
  const existingIds = new Set(DOMAIN_PRESETS.map((p) => p.id));
  const added: FullPreset[] = [];
  for (const preset of presets) {
    if (!existingIds.has(preset.id)) {
      DOMAIN_PRESETS.push(preset);
      existingIds.add(preset.id);
      added.push(preset);
    }
  }
  // Eagerly push the newly registered presets into the live store
  // state. Without this, registrations that land *after* the store
  // has already been created/initialized never reach the UI — the
  // module-level `DOMAIN_PRESETS` array grows but `fullPresets`
  // stays frozen on whatever was present when `create()` ran. That
  // showed up as "compass cost heatmap missing from Lens picker"
  // even though `COMPASS_VIEW_PRESETS` had 15 entries.
  if (added.length === 0) return;
  try {
    const state = useViewRulesStore.getState();
    const existingPresetIds = new Set(state.fullPresets.map((p) => p.id));
    const merged = [...state.fullPresets];
    for (const p of added) {
      if (!existingPresetIds.has(p.id)) merged.push(p);
    }
    useViewRulesStore.setState({ fullPresets: merged });
  } catch {
    // Store may not exist yet (module evaluation order). The next
    // initialize() will pick up DOMAIN_PRESETS via allBuiltInPresets().
  }
}

function allBuiltInPresets(): FullPreset[] {
  return [...BUILT_IN_PRESETS, ...DOMAIN_PRESETS];
}

/**
 * Filter presets by available data fields.
 * Presets with no requiredFields (or empty array) always pass.
 * Uses `some` — preset is shown if ANY required field is present.
 */
export const filterPresetsByFields = (
  presets: FullPreset[],
  availableFieldIds: Set<string>,
): FullPreset[] =>
  presets.filter((p) => {
    if (!p.requiredFields || p.requiredFields.length === 0) return true;
    return p.requiredFields.some((f) => availableFieldIds.has(f));
  });

/**
 * Filter presets by domain. Returns presets where `domain` is undefined
 * (domain-agnostic) or matches the given domain string.
 */
export const filterPresetsByDomain = (
  presets: FullPreset[],
  domain: string | null,
): FullPreset[] =>
  presets.filter((p) => !p.domain || p.domain === domain);

// =============================================================================
// Yjs Storage Helpers
// =============================================================================

const VIEW_RULES_ORIGIN = 'viewRules-save';

interface StoredViewRules {
  rules?: ViewRule[];
  customPresets?: FullPreset[];
  presets?: ViewRulePreset[];
  precomputedMatchingNodeIds?: string[];
  precomputedChainNodeIds?: string[];
  precomputedChainEdgeIds?: string[];
}

const loadViewRulesFromYDoc = (doc: Y.Doc): StoredViewRules => {
  try {
    const map = doc.getMap('viewRules');
    const result: StoredViewRules = {};

    const rulesJson = map.get('rules') as string | undefined;
    if (rulesJson) {
      result.rules = JSON.parse(rulesJson);
    }

    const customPresetsJson = map.get('customPresets') as string | undefined;
    if (customPresetsJson) {
      result.customPresets = JSON.parse(customPresetsJson);
    }

    const presetsJson = map.get('presets') as string | undefined;
    if (presetsJson) {
      result.presets = JSON.parse(presetsJson);
    }

    // Pre-computed filter IDs from server-generated views (department views, etc.)
    const matchingJson = map.get('precomputedMatchingNodeIds') as string | undefined;
    if (matchingJson) {
      result.precomputedMatchingNodeIds = JSON.parse(matchingJson);
    }
    const chainNodesJson = map.get('precomputedChainNodeIds') as string | undefined;
    if (chainNodesJson) {
      result.precomputedChainNodeIds = JSON.parse(chainNodesJson);
    }
    const chainEdgesJson = map.get('precomputedChainEdgeIds') as string | undefined;
    if (chainEdgesJson) {
      result.precomputedChainEdgeIds = JSON.parse(chainEdgesJson);
    }

    return result;
  } catch (e) {
    console.error('[ViewRulesStore] Failed to load from YDoc:', e);
    return {};
  }
};

const saveRulesToYDoc = (doc: Y.Doc, rules: ViewRule[]) => {
  try {
    const map = doc.getMap('viewRules');
    doc.transact(() => {
      map.set('rules', JSON.stringify(rules));
    }, VIEW_RULES_ORIGIN);
  } catch (e) {
    console.error('[ViewRulesStore] Failed to save rules to YDoc:', e);
  }
};

const saveCustomPresetsToYDoc = (doc: Y.Doc, presets: FullPreset[]) => {
  try {
    const map = doc.getMap('viewRules');
    doc.transact(() => {
      map.set('customPresets', JSON.stringify(presets));
    }, VIEW_RULES_ORIGIN);
  } catch (e) {
    console.error('[ViewRulesStore] Failed to save custom presets to YDoc:', e);
  }
};

const savePresetsToYDoc = (doc: Y.Doc, presets: ViewRulePreset[]) => {
  try {
    const map = doc.getMap('viewRules');
    doc.transact(() => {
      map.set('presets', JSON.stringify(presets));
    }, VIEW_RULES_ORIGIN);
  } catch (e) {
    console.error('[ViewRulesStore] Failed to save presets to YDoc:', e);
  }
};

export { stripBlockedDisplayFields };

// =============================================================================
// Store State Interface
// =============================================================================

interface ViewRulesState {
  /** Current diagram ID */
  diagramId: string | null;

  /** Reference to the Y.Doc for collaborative storage */
  ydoc: Y.Doc | null;

  /** Cleanup function for the Yjs observer */
  _observerCleanup: (() => void) | null;

  /** All view rules including the primary filter — what this viewer renders. */
  rules: ViewRule[];

  /**
   * The chart's default lens: rules exactly as they stand in the shared
   * document. Kept alongside `rules` so a viewer holding their own lens can
   * drop it and get the default back, and so a collaborator changing the
   * default doesn't yank the lens out from under them.
   */
  docRules: ViewRule[];

  /**
   * True while `rules` is this viewer's own view rather than the shared
   * default. Nothing written while this is set reaches the document — that is
   * what makes a `?lens=` link, or an agent applying a lens to answer a
   * question, a private act. `promoteLocalViewToDefault` is the one way out.
   */
  isLocalView: boolean;

  /**
   * Display fields this viewer has no grant for, published by the domain that
   * knows (for org charts, `useOrgAnalyticsLens` from the server's per-viewer
   * lens eligibility). Rules are filtered against these at read time rather
   * than deleted: the document keeps the default it was given, and a viewer
   * who cannot see `salary` simply never learns the chart is keyed on it.
   */
  blockedDisplayFields: string[];

  /** Computed matching node IDs (set externally after evaluation) */
  matchingNodeIds: Set<string>;

  /** Reporting chain node IDs (nodes on path from matching nodes to root) */
  reportingChainNodeIds: Set<string>;

  /** Reporting chain edge IDs */
  reportingChainEdgeIds: Set<string>;

  /** Saved presets (single filter configs) */
  presets: ViewRulePreset[];

  /** Full presets (entire rules configuration) */
  fullPresets: FullPreset[];

  /** Active smart group ID - when set, this smart group's filter is applied */
  activeSmartGroupId: string | null;

  /** Active full preset ID - when set, the rules panel is collapsed */
  activeFullPresetId: string | null;

  // ========== Initialization ==========

  /** Initialize with diagram context and Yjs doc for persistence */
  initialize: (diagramId: string, ydoc: Y.Doc | null) => void;

  // ========== Primary Filter Actions ==========

  /** Add a condition to the primary filter */
  addCondition: (condition: Omit<ViewCondition, 'id'>) => void;

  /** Update a condition in the primary filter */
  updateCondition: (id: string, updates: Partial<Omit<ViewCondition, 'id'>>) => void;

  /** Remove a condition from the primary filter */
  removeCondition: (id: string) => void;

  /** Set the logic (AND/OR) for the primary filter */
  setLogic: (logic: 'and' | 'or') => void;

  /** Set the filter effect (none/dim/hide) for non-matching nodes */
  setFilterEffect: (effect: FilterEffect) => void;

  /** Toggle showing reporting lines */
  setShowReportingLines: (show: boolean) => void;

  /** Clear the primary filter (remove all conditions) */
  clearFilter: () => void;

  /** Set display name for primary filter */
  setPrimaryFilterName: (name: string) => void;

  /** Set the active smart group (collapses builder, applies group's filter) */
  setActiveSmartGroup: (id: string | null) => void;

  /** Clear active smart group and optionally expand builder with its conditions */
  editActiveSmartGroup: () => void;

  // ========== Computed Data Setters ==========

  /** Set matching node IDs (called after evaluation) */
  setMatchingNodeIds: (ids: Set<string>) => void;

  /** Set reporting chain node IDs */
  setReportingChainNodeIds: (ids: Set<string>) => void;

  /** Set reporting chain edge IDs */
  setReportingChainEdgeIds: (ids: Set<string>) => void;

  // ========== Display Rules Actions ==========

  /** Add a new display rule by type (legacy) */
  addRule: (type: DisplayRuleType, name?: string) => string;

  /** Add a new view rule by kind */
  addViewRule: (kind: ViewRuleKind, options?: { field?: string; name?: string; minColor?: string; maxColor?: string }) => string;

  /** Update a display rule */
  updateRule: (id: string, updates: Partial<Omit<ViewRule, 'id'>>) => void;

  /** Remove a display rule */
  removeRule: (id: string) => void;

  /** Toggle a rule's enabled state */
  toggleRule: (id: string) => void;

  /** Reorder rules */
  reorderRules: (fromIndex: number, toIndex: number) => void;

  /** Add a condition to a display rule */
  addConditionToRule: (ruleId: string, condition: Omit<ViewCondition, 'id'>) => void;

  /** Update a condition within a display rule */
  updateConditionInRule: (
    ruleId: string,
    conditionId: string,
    updates: Partial<Omit<ViewCondition, 'id'>>
  ) => void;

  /** Remove a condition from a display rule */
  removeConditionFromRule: (ruleId: string, conditionId: string) => void;

  /** Update display options for a rule */
  updateRuleDisplay: (ruleId: string, display: Partial<DisplayOptions>) => void;

  /** Update display options for this viewer without changing the shared chart default. */
  updateRuleDisplayLocal: (ruleId: string, display: Partial<DisplayOptions>) => void;

  // ========== Preset Actions ==========

  /** Save current primary filter as a preset */
  saveAsPreset: (name: string, description?: string) => string;

  /** Load a preset into the primary filter */
  loadPreset: (presetId: string) => void;

  /** Delete a preset */
  deletePreset: (presetId: string) => void;

  // ========== Full Preset Actions ==========

  /** Save all current rules as a full preset */
  saveFullPreset: (name: string) => string;

  /**
   * Load a full preset (replaces all rules).
   *
   * Persists the resulting rules to the Y.Doc by default, because picking a
   * viewpoint is normally a deliberate edit to the shared document. Pass
   * `{ persist: false }` for view state that belongs to one viewer — a `?lens=`
   * deep link, or an agent applying a lens to answer a question — so it does
   * not recolour the chart for every other collaborator.
   */
  loadFullPreset: (presetId: string, options?: { persist?: boolean }) => void;

  /** Delete a full preset */
  deleteFullPreset: (presetId: string) => void;

  /** Clear active full preset and enter edit mode */
  editActiveFullPreset: () => void;

  // ========== Local View Actions ==========

  /**
   * Promote this viewer's local view to the chart default, so everyone opening
   * the chart sees it. The deliberate counterpart to the transient apply —
   * callers must gate this on write access.
   */
  promoteLocalViewToDefault: () => void;

  /** Drop the local view and fall back to the chart default. */
  clearLocalView: () => void;

  /** Publish the display fields this viewer has no grant for. */
  setBlockedDisplayFields: (fields: string[]) => void;

  // ========== Selectors ==========

  /** Get the primary filter rule */
  getPrimaryFilter: () => ViewRule;

  /** Get display rules (excludes primary filter) */
  getDisplayRules: () => ViewRule[];

  /** Check if filter is active (has conditions) */
  isFilterActive: () => boolean;
}

/**
 * The single place rule changes reach the shared document.
 *
 * Every mutation routes through here, so the local-view rule holds globally: a
 * viewer who applies a lens and then edits a rule keeps both to themselves.
 * Previously each mutation called `saveRulesToYDoc` directly, which meant any
 * one of them could carry a transient lens into the document.
 */
const commitRules = (
  set: (partial: Partial<ViewRulesState>) => void,
  get: () => ViewRulesState,
  rulesToPersist?: ViewRule[],
): void => {
  const { ydoc, rules, isLocalView } = get();
  if (isLocalView) return;
  const next = rulesToPersist ?? rules;
  set({ docRules: next });
  if (ydoc) saveRulesToYDoc(ydoc, next);
};

/**
 * Display editors update one nested option at a time. Merge those option
 * objects independently so changing a field does not discard metadata owned
 * by another feature (for example Succession Planning's semantic palette and
 * successor visibility mode).
 */
const mergeDisplayOptions = (
  current: DisplayOptions,
  updates: Partial<DisplayOptions>,
): DisplayOptions => ({
  ...current,
  ...updates,
  ...(updates.visibility ? { visibility: { ...current.visibility, ...updates.visibility } } : {}),
  ...(updates.colors ? { colors: { ...current.colors, ...updates.colors } } : {}),
  ...(updates.badge ? { badge: { ...current.badge, ...updates.badge } } : {}),
  ...(updates.label ? { label: { ...current.label, ...updates.label } } : {}),
  ...(updates.heatmap ? { heatmap: { ...current.heatmap, ...updates.heatmap } } : {}),
  ...(updates.colorByField ? { colorByField: { ...current.colorByField, ...updates.colorByField } } : {}),
  ...(updates.secondaryReportsLens
    ? { secondaryReportsLens: { ...current.secondaryReportsLens, ...updates.secondaryReportsLens } }
    : {}),
  ...(updates.successorLens ? { successorLens: { ...current.successorLens, ...updates.successorLens } } : {}),
});

export const useViewRulesStore = create<ViewRulesState>((set, get) => ({
  diagramId: null,
  ydoc: null,
  _observerCleanup: null,
  rules: [],
  docRules: [],
  isLocalView: false,
  blockedDisplayFields: [],
  matchingNodeIds: new Set(),
  reportingChainNodeIds: new Set(),
  reportingChainEdgeIds: new Set(),
  presets: [],
  fullPresets: allBuiltInPresets(),
  activeSmartGroupId: null,
  activeFullPresetId: null,

  // ========== Initialization ==========

  initialize: (diagramId, ydoc) => {
    const { _observerCleanup: prevCleanup } = get();
    if (prevCleanup) prevCleanup();

    const stored = ydoc ? loadViewRulesFromYDoc(ydoc) : {};
    const loadedRules = stored.rules ?? [];
    const rules = loadedRules.length > 0 ? loadedRules : [createPrimaryFilterRule()];
    const customPresets = stored.customPresets ?? [];
    const presets = stored.presets ?? [];

    let cleanup: (() => void) | null = null;

    if (ydoc) {
      const viewRulesMap = ydoc.getMap('viewRules');
      const observer = (_events: Y.YMapEvent<unknown>, txn: Y.Transaction) => {
        if (txn.origin === VIEW_RULES_ORIGIN) return;
        const reloaded = loadViewRulesFromYDoc(ydoc);
        const reloadedCustom = reloaded.customPresets ?? [];
        const reloadedRules = reloaded.rules ?? [];
        const nextDocRules =
          reloadedRules.length > 0 ? reloadedRules : [createPrimaryFilterRule()];
        set({
          docRules: nextDocRules,
          // A viewer holding their own lens keeps it — only the chart default
          // moved. Without this guard, a collaborator changing the default
          // yanks the lens out from under everyone currently looking through one.
          ...(get().isLocalView ? {} : { rules: nextDocRules }),
          fullPresets: [...allBuiltInPresets(), ...reloadedCustom],
          presets: reloaded.presets ?? [],
        });
      };
      viewRulesMap.observe(observer);
      cleanup = () => viewRulesMap.unobserve(observer);
    }

    // Use pre-computed IDs from server-generated views (instant filter on first load)
    const precomputedMatching = stored.precomputedMatchingNodeIds
      ? new Set(stored.precomputedMatchingNodeIds)
      : new Set<string>();
    const precomputedChainNodes = stored.precomputedChainNodeIds
      ? new Set(stored.precomputedChainNodeIds)
      : new Set<string>();
    const precomputedChainEdges = stored.precomputedChainEdgeIds
      ? new Set(stored.precomputedChainEdgeIds)
      : new Set<string>();

    set({
      diagramId,
      ydoc,
      _observerCleanup: cleanup,
      rules,
      docRules: rules,
      // Opening a chart always starts on its default, never on the lens the
      // viewer happened to be holding on the previous one.
      isLocalView: false,
      fullPresets: [...allBuiltInPresets(), ...customPresets],
      presets,
      matchingNodeIds: precomputedMatching,
      reportingChainNodeIds: precomputedChainNodes,
      reportingChainEdgeIds: precomputedChainEdges,
      activeSmartGroupId: null,
      activeFullPresetId: null,
      // `blockedDisplayFields` is deliberately not cleared: eligibility is
      // per-viewer, and holding the previous answer until the new one arrives
      // over-blocks for a moment rather than briefly exposing a field.
    });
  },

  // ========== Primary Filter Actions ==========

  addCondition: (condition) => {
    const newCondition: ViewCondition = {
      ...condition,
      id: generateId('cond'),
    };

    set((state) => ({
      rules: state.rules.map((rule) =>
        rule.id === PRIMARY_FILTER_ID
          ? { ...rule, enabled: true, conditions: [...rule.conditions, newCondition] }
          : rule
      ),
      activeSmartGroupId: null, // Clear when editing manually
    }));
    commitRules(set, get);
  },

  updateCondition: (id, updates) => {
    set((state) => ({
      rules: state.rules.map((rule) =>
        rule.id === PRIMARY_FILTER_ID
          ? {
              ...rule,
              conditions: rule.conditions.map((c) =>
                c.id === id ? { ...c, ...updates } : c
              ),
            }
          : rule
      ),
      activeSmartGroupId: null, // Clear when editing manually
    }));
    commitRules(set, get);
  },

  removeCondition: (id) => {
    set((state) => ({
      rules: state.rules.map((rule) =>
        rule.id === PRIMARY_FILTER_ID
          ? {
              ...rule,
              conditions: rule.conditions.filter((c) => c.id !== id),
            }
          : rule
      ),
      activeSmartGroupId: null, // Clear when editing manually
    }));
    commitRules(set, get);
  },

  setLogic: (logic) => {
    set((state) => ({
      rules: state.rules.map((rule) =>
        rule.id === PRIMARY_FILTER_ID ? { ...rule, logic } : rule
      ),
    }));
    commitRules(set, get);
  },

  setFilterEffect: (effect) => {
    set((state) => ({
      rules: state.rules.map((rule) =>
        rule.id === PRIMARY_FILTER_ID
          ? {
              ...rule,
              display: {
                ...rule.display,
                visibility: {
                  ...rule.display.visibility,
                  filterEffect: effect,
                  showReportingLines: rule.display.visibility?.showReportingLines ?? false,
                },
              },
            }
          : rule
      ),
    }));
    commitRules(set, get);
  },

  setShowReportingLines: (show) => {
    set((state) => ({
      rules: state.rules.map((rule) =>
        rule.id === PRIMARY_FILTER_ID
          ? {
              ...rule,
              display: {
                ...rule.display,
                visibility: {
                  filterEffect: rule.display.visibility?.filterEffect ?? 'dim',
                  showReportingLines: show,
                },
              },
            }
          : rule
      ),
    }));
    commitRules(set, get);
  },

  clearFilter: () => {
    set((state) => ({
      rules: state.rules.map((rule) =>
        rule.id === PRIMARY_FILTER_ID
          ? {
              ...rule,
              conditions: [],
            }
          : rule
      ),
      matchingNodeIds: new Set(),
      reportingChainNodeIds: new Set(),
      reportingChainEdgeIds: new Set(),
      activeSmartGroupId: null,
    }));
    commitRules(set, get);
  },

  setPrimaryFilterName: (name) => {
    set((state) => ({
      rules: state.rules.map((rule) =>
        rule.id === PRIMARY_FILTER_ID
          ? {
              ...rule,
              name,
            }
          : rule
      ),
    }));
    commitRules(set, get);
  },

  setActiveSmartGroup: (id) => {
    set({ activeSmartGroupId: id });
  },

  editActiveSmartGroup: () => {
    set({ activeSmartGroupId: null });
  },

  // ========== Computed Data Setters ==========

  setMatchingNodeIds: (ids) => set({ matchingNodeIds: ids }),

  setReportingChainNodeIds: (ids) => set({ reportingChainNodeIds: ids }),

  setReportingChainEdgeIds: (ids) => set({ reportingChainEdgeIds: ids }),

  // ========== Display Rules Actions ==========

  addRule: (type, name) => {
    const { rules } = get();
    const displayRules = rules.filter((r) => r.id !== PRIMARY_FILTER_ID);
    const order = displayRules.length + 1;
    const ruleName = name ?? `${type.charAt(0).toUpperCase() + type.slice(1)} Rule`;
    const newRule = createDisplayRule(type, ruleName, order);

    set((state) => ({
      rules: [...state.rules, newRule],
    }));

    commitRules(set, get);

    return newRule.id;
  },

  addViewRule: (kind, options = {}) => {
    const { rules } = get();
    const order = rules.length; // After all existing rules

    let newRule: ViewRule;
    switch (kind) {
      case 'filter':
        newRule = createFilterRule(options.name || 'Filter', order);
        break;
      case 'heatmap':
        newRule = createHeatmapRule(options.field || 'tenure', order, options.minColor, options.maxColor);
        break;
      case 'colorBy':
        newRule = createColorByRule(options.field || 'department', order);
        break;
      case 'badge':
        newRule = createBadgeRule(order);
        break;
      case 'highlight':
        newRule = createHighlightRule(order);
        break;
      case 'label':
        newRule = createLabelRule(order);
        break;
      default:
        return '';
    }

    newRule = resolveOrgAnalyticRules([newRule])[0];
    if (options.name && kind !== 'filter') {
      newRule.name = options.name;
    }

    // Auto-disable conflicting rules when adding heatmap/colorBy
    if (newRule.display.heatmap || newRule.display.colorByField) {
      set((state) => ({
        rules: [
          ...state.rules.map((r) =>
            r.id !== PRIMARY_FILTER_ID && (getRuleKind(r) === 'heatmap' || getRuleKind(r) === 'colorBy') ? { ...r, enabled: false } : r
          ),
          newRule,
        ],
      }));
    } else {
      set((state) => ({
        rules: [...state.rules, newRule],
      }));
    }

    commitRules(set, get);

    return newRule.id;
  },

  updateRule: (id, updates) => {
    if (id === PRIMARY_FILTER_ID) return; // Use specific actions for primary filter

    set((state) => ({
      rules: state.rules.map((rule) =>
        rule.id === id ? { ...rule, ...updates } : rule
      ),
    }));
    commitRules(set, get);
  },

  removeRule: (id) => {
    set((state) => ({
      rules: state.rules.filter((rule) => rule.id !== id),
    }));
    commitRules(set, get);
  },

  toggleRule: (id) => {
    const rule = get().rules.find((r) => r.id === id);
    if (!rule) return;

    const kind = getRuleKind(rule);
    const newEnabled = !rule.enabled;

    // Auto-disable conflicting rules (only one heatmap/colorBy active at a time)
    if (newEnabled && (kind === 'heatmap' || kind === 'colorBy')) {
      set((state) => ({
        rules: state.rules.map((r) => {
          if (r.id === id) return { ...r, enabled: true };
          if (getRuleKind(r) === kind) return { ...r, enabled: false };
          return r;
        }),
      }));
    } else {
      // Normal toggle
      set((state) => ({
        rules: state.rules.map((r) =>
          r.id === id ? { ...r, enabled: newEnabled } : r
        ),
      }));
    }
    commitRules(set, get);
  },

  reorderRules: (fromIndex, toIndex) => {
    set((state) => {
      const displayRules = state.rules.filter((r) => r.id !== PRIMARY_FILTER_ID);
      const primaryFilter = state.rules.find((r) => r.id === PRIMARY_FILTER_ID)!;

      const reordered = [...displayRules];
      const [removed] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, removed);

      // Update order numbers
      const updatedRules = reordered.map((rule, index) => ({
        ...rule,
        order: index + 1,
      }));

      return {
        rules: [primaryFilter, ...updatedRules],
      };
    });
    commitRules(set, get);
  },

  addConditionToRule: (ruleId, condition) => {
    const newCondition: ViewCondition = {
      ...condition,
      id: generateId('cond'),
    };

    set((state) => ({
      rules: state.rules.map((rule) =>
        rule.id === ruleId
          ? { ...rule, enabled: true, conditions: [...rule.conditions, newCondition] }
          : rule
      ),
    }));
    commitRules(set, get);
  },

  updateConditionInRule: (ruleId, conditionId, updates) => {
    set((state) => ({
      rules: state.rules.map((rule) =>
        rule.id === ruleId
          ? {
              ...rule,
              conditions: rule.conditions.map((c) =>
                c.id === conditionId ? { ...c, ...updates } : c
              ),
            }
          : rule
      ),
    }));
    commitRules(set, get);
  },

  removeConditionFromRule: (ruleId, conditionId) => {
    set((state) => ({
      rules: state.rules.map((rule) =>
        rule.id === ruleId
          ? {
              ...rule,
              conditions: rule.conditions.filter((c) => c.id !== conditionId),
            }
          : rule
      ),
    }));
    commitRules(set, get);
  },

  updateRuleDisplay: (ruleId, display) => {
    set((state) => ({
      rules: state.rules.map((rule) =>
        rule.id === ruleId
          ? {
              ...rule,
              display: mergeDisplayOptions(rule.display, display),
            }
          : rule
      ),
    }));
    commitRules(set, get);
  },

  updateRuleDisplayLocal: (ruleId, display) => {
    set((state) => ({
      rules: state.rules.map((rule) =>
        rule.id === ruleId
          ? {
              ...rule,
              display: mergeDisplayOptions(rule.display, display),
            }
          : rule
      ),
      isLocalView: true,
    }));
  },

  // ========== Preset Actions ==========

  saveAsPreset: (name, description) => {
    const { rules } = get();
    const primaryFilter = rules.find((r) => r.id === PRIMARY_FILTER_ID);
    if (!primaryFilter || primaryFilter.conditions.length === 0) {
      return '';
    }

    const presetId = generateId('preset');
    const preset: ViewRulePreset = {
      id: presetId,
      name,
      description,
      rule: {
        name: primaryFilter.name,
        conditions: [...primaryFilter.conditions],
        logic: primaryFilter.logic,
        enabled: true,
        display: { ...primaryFilter.display },
      },
      createdAt: Date.now(),
    };

    set((state) => ({
      presets: [...state.presets, preset],
    }));

    const { ydoc, presets: updatedPresets } = get();
    if (ydoc) savePresetsToYDoc(ydoc, updatedPresets);

    return presetId;
  },

  loadPreset: (presetId) => {
    const { presets } = get();
    const preset = presets.find((p) => p.id === presetId);
    if (!preset) return;

    set((state) => ({
      rules: state.rules.map((rule) =>
        rule.id === PRIMARY_FILTER_ID
          ? {
              ...rule,
              conditions: preset.rule.conditions.map((c) => ({
                ...c,
                id: generateId('cond'), // Generate new IDs for loaded conditions
              })),
              logic: preset.rule.logic,
              display: {
                ...rule.display,
                visibility: preset.rule.display.visibility ?? rule.display.visibility,
              },
            }
          : rule
      ),
    }));
    commitRules(set, get);
  },

  deletePreset: (presetId) => {
    set((state) => ({
      presets: state.presets.filter((p) => p.id !== presetId),
    }));
    const { ydoc, presets } = get();
    if (ydoc) savePresetsToYDoc(ydoc, presets);
  },

  // ========== Full Preset Actions ==========

  saveFullPreset: (name) => {
    const { rules } = get();
    const presetId = generateId('full-preset');

    const preset: FullPreset = {
      id: presetId,
      name,
      rules: rules.map((r) => ({
        name: r.name,
        conditions: [...r.conditions],
        logic: r.logic,
        enabled: r.enabled,
        display: { ...r.display },
        order: r.order,
      })),
      createdAt: Date.now(),
    };

    set((state) => ({
      fullPresets: [...state.fullPresets, preset],
      activeFullPresetId: presetId, // Collapse into the new preset
    }));

    const { ydoc, fullPresets } = get();
    if (ydoc) saveCustomPresetsToYDoc(ydoc, fullPresets.filter((p) => !p.id.startsWith('builtin-')));

    return presetId;
  },

  loadFullPreset: (presetId, options) => {
    const { fullPresets } = get();
    const preset = fullPresets.find((p) => p.id === presetId);
    if (!preset) return;

    // Recreate rules with new IDs
    const newRules = preset.rules.map((r) => ({
      ...r,
      id: r.order === 0 ? PRIMARY_FILTER_ID : generateId('rule'),
      conditions: r.conditions.map((c) => ({
        ...c,
        id: generateId('cond'),
      })),
    }));

    const localOnly = options?.persist === false;
    set({
      rules: newRules,
      activeSmartGroupId: null,
      activeFullPresetId: presetId,
      isLocalView: localOnly,
    });
    if (localOnly) return;
    commitRules(set, get);
  },

  deleteFullPreset: (presetId) => {
    if (presetId.startsWith('builtin-')) return; // Built-in presets cannot be deleted
    set((state) => ({
      fullPresets: state.fullPresets.filter((p) => p.id !== presetId),
      // Clear active preset if we're deleting the active one
      activeFullPresetId: state.activeFullPresetId === presetId ? null : state.activeFullPresetId,
    }));
    const { ydoc, fullPresets } = get();
    if (ydoc) saveCustomPresetsToYDoc(ydoc, fullPresets.filter((p) => !p.id.startsWith('builtin-')));
  },

  editActiveFullPreset: () => {
    set({ activeFullPresetId: null });
  },

  // ========== Local View Actions ==========

  promoteLocalViewToDefault: () => {
    const { rules, ydoc, isLocalView } = get();
    if (!isLocalView) return;
    // Clear the flag first so `commitRules` — which refuses to write while a
    // local view is held — treats this as the shared edit it now is.
    set({ isLocalView: false });
    if (!ydoc) {
      set({ docRules: rules });
      return;
    }
    commitRules(set, get, rules);
  },

  clearLocalView: () => {
    const { docRules, isLocalView } = get();
    if (!isLocalView) return;
    set({ rules: docRules, isLocalView: false, activeFullPresetId: null });
  },

  setBlockedDisplayFields: (fields) => {
    const { blockedDisplayFields } = get();
    const next = [...fields].sort();
    // Zustand hands every subscriber a new array otherwise, and the rule
    // filtering downstream is memoized on this reference.
    if (
      next.length === blockedDisplayFields.length &&
      next.every((f, i) => f === blockedDisplayFields[i])
    ) {
      return;
    }
    set({ blockedDisplayFields: next });
  },

  // ========== Selectors ==========

  // Both selectors read through the eligibility filter, so an agent answering
  // on this viewer's behalf sees exactly what the viewer sees — no more.

  getPrimaryFilter: () => {
    const { rules, blockedDisplayFields } = get();
    const visible = stripBlockedDisplayFields(rules, blockedDisplayFields);
    return visible.find((r) => r.id === PRIMARY_FILTER_ID) ?? createPrimaryFilterRule();
  },

  getDisplayRules: () => {
    const { rules, blockedDisplayFields } = get();
    return stripBlockedDisplayFields(rules, blockedDisplayFields)
      .filter((r) => r.id !== PRIMARY_FILTER_ID)
      .sort((a, b) => a.order - b.order);
  },

  isFilterActive: () => {
    const { rules } = get();
    const primaryFilter = rules.find((r) => r.id === PRIMARY_FILTER_ID);
    return primaryFilter !== undefined && primaryFilter.conditions.length > 0;
  },
}));

// ========== Convenience Hooks ==========

const defaultPrimaryFilter = createPrimaryFilterRule();

/**
 * Hook to access primary filter state
 */
export const usePrimaryFilter = () => {
  const rules = useViewRulesStore((s) => s.rules);
  const addCondition = useViewRulesStore((s) => s.addCondition);
  const updateCondition = useViewRulesStore((s) => s.updateCondition);
  const removeCondition = useViewRulesStore((s) => s.removeCondition);
  const setLogic = useViewRulesStore((s) => s.setLogic);
  const setFilterEffect = useViewRulesStore((s) => s.setFilterEffect);
  const setShowReportingLines = useViewRulesStore((s) => s.setShowReportingLines);
  const clearFilter = useViewRulesStore((s) => s.clearFilter);
  const matchingNodeIds = useViewRulesStore((s) => s.matchingNodeIds);
  const reportingChainNodeIds = useViewRulesStore((s) => s.reportingChainNodeIds);
  const reportingChainEdgeIds = useViewRulesStore((s) => s.reportingChainEdgeIds);
  const activeSmartGroupId = useViewRulesStore((s) => s.activeSmartGroupId);
  const setActiveSmartGroup = useViewRulesStore((s) => s.setActiveSmartGroup);
  const editActiveSmartGroup = useViewRulesStore((s) => s.editActiveSmartGroup);

  const primaryFilter = useMemo(
    () => rules.find((r) => r.id === PRIMARY_FILTER_ID) ?? defaultPrimaryFilter,
    [rules]
  );

  return {
    // State
    conditions: primaryFilter.conditions,
    logic: primaryFilter.logic,
    filterEffect: primaryFilter.display.visibility?.filterEffect ?? 'dim',
    showReportingLines: primaryFilter.display.visibility?.showReportingLines ?? false,
    matchingNodeIds,
    reportingChainNodeIds,
    reportingChainEdgeIds,
    isActive: primaryFilter.conditions.length > 0,
    activeSmartGroupId,

    // Actions
    addCondition,
    updateCondition,
    removeCondition,
    setLogic,
    setFilterEffect,
    setShowReportingLines,
    clearFilter,
    setActiveSmartGroup,
    editActiveSmartGroup,
  };
};

/**
 * Hook to access display rules state
 */
export const useDisplayRules = () => {
  const rules = useViewRulesStore((s) => s.rules);
  const addRule = useViewRulesStore((s) => s.addRule);
  const updateRule = useViewRulesStore((s) => s.updateRule);
  const removeRule = useViewRulesStore((s) => s.removeRule);
  const toggleRule = useViewRulesStore((s) => s.toggleRule);
  const reorderRules = useViewRulesStore((s) => s.reorderRules);
  const addConditionToRule = useViewRulesStore((s) => s.addConditionToRule);
  const updateConditionInRule = useViewRulesStore((s) => s.updateConditionInRule);
  const removeConditionFromRule = useViewRulesStore((s) => s.removeConditionFromRule);
  const updateRuleDisplay = useViewRulesStore((s) => s.updateRuleDisplay);
  const updateRuleDisplayLocal = useViewRulesStore((s) => s.updateRuleDisplayLocal);

  const displayRules = useMemo(
    () => rules.filter((r) => r.id !== PRIMARY_FILTER_ID).sort((a, b) => a.order - b.order),
    [rules]
  );

  const hasActiveRules = useMemo(
    () => displayRules.some((r) => r.enabled),
    [displayRules]
  );

  return {
    rules: displayRules,
    hasActiveRules,
    addRule,
    updateRule,
    removeRule,
    toggleRule,
    reorderRules,
    addConditionToRule,
    updateConditionInRule,
    removeConditionFromRule,
    updateRuleDisplay,
    updateRuleDisplayLocal,
  };
};

/**
 * Hook to access all view rules (including primary filter) sorted by order
 */
export const useAllRules = () => {
  const rules = useViewRulesStore((s) => s.rules);
  const addViewRule = useViewRulesStore((s) => s.addViewRule);
  const updateRule = useViewRulesStore((s) => s.updateRule);
  const removeRule = useViewRulesStore((s) => s.removeRule);
  const toggleRule = useViewRulesStore((s) => s.toggleRule);
  const updateRuleDisplay = useViewRulesStore((s) => s.updateRuleDisplay);
  const updateRuleDisplayLocal = useViewRulesStore((s) => s.updateRuleDisplayLocal);
  const addConditionToRule = useViewRulesStore((s) => s.addConditionToRule);
  const updateConditionInRule = useViewRulesStore((s) => s.updateConditionInRule);
  const removeConditionFromRule = useViewRulesStore((s) => s.removeConditionFromRule);
  const fullPresets = useViewRulesStore((s) => s.fullPresets);
  const saveFullPreset = useViewRulesStore((s) => s.saveFullPreset);
  const loadFullPreset = useViewRulesStore((s) => s.loadFullPreset);
  const deleteFullPreset = useViewRulesStore((s) => s.deleteFullPreset);
  const activeFullPresetId = useViewRulesStore((s) => s.activeFullPresetId);
  const editActiveFullPreset = useViewRulesStore((s) => s.editActiveFullPreset);

  const sortedRules = useMemo(
    () => [...rules].sort((a, b) => a.order - b.order),
    [rules]
  );

  const activeFullPreset = useMemo(
    () => fullPresets.find((p) => p.id === activeFullPresetId),
    [fullPresets, activeFullPresetId]
  );

  return {
    rules: sortedRules,
    addViewRule,
    updateRule,
    updateRuleDisplayLocal,
    removeRule,
    toggleRule,
    updateRuleDisplay,
    addConditionToRule,
    updateConditionInRule,
    removeConditionFromRule,
    fullPresets,
    saveFullPreset,
    loadFullPreset,
    deleteFullPreset,
    activeFullPresetId,
    activeFullPreset,
    editActiveFullPreset,
  };
};
